import { QuoteHeader, QuoteItem, Position } from "@shared/schema";
import { type PersistedQuoteEditorState, normalizePersistedQuoteEditorState } from "@shared/quote-editor-state";
import { getQuoteCustomerName, getQuoteOfferNumber, getQuoteProjectName } from "@shared/quote-naming";
import { healthCheckService } from './health-check-service';
import { computeQuoteTotals } from './quote-calculations';

export interface QuoteDataPackage {
  header: QuoteHeader;
  items: QuoteItem[]; // Selected items with quantities > 0
  positions: Position[]; // All available positions from the catalog
  gewerkOrder: string[];
  editorState?: PersistedQuoteEditorState;
  metadata: {
    filename?: string;
    customerName?: string;
    projectName?: string;
    offerNumber?: string;
    timestamp?: string;
    version?: number;
    itemCount?: number;
    totalValue?: number;
    totalPositions?: number;
    slot?: number;
    storageRevision?: number;
  };
}

export interface SaveQuoteRequest {
  header: QuoteHeader;
  items: QuoteItem[];
  positions: Position[];
  gewerkOrder: string[];
  filename?: string;
  targetSlot?: number;
  expectedStorageRevision?: number;
  editorState: PersistedQuoteEditorState;
}

export interface DuplicateQuoteResult {
  sourceSlot: number;
  newSlot: number;
  quote: QuoteDataPackage;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp?: string;
}

// Get server URL based on environment
function getServerUrl(): string {
  // Store-Loader laufen auch in fokussierten Node-Tests mit einem kontrollierten fetch-Stub.
  // Dort existiert weder window noch Vites import.meta.env; relative API-Pfade bleiben korrekt.
  if (typeof window === 'undefined') {
    return '';
  }
  // In development with Vite proxy, use relative URLs
  if (import.meta.env.DEV) {
    return '';
  }
  
  // In production, use the current origin
  return window.location.origin;
}

// Optimized function to test server connectivity - now uses cached health check
export async function testServerConnection(): Promise<{success: boolean, error?: string, details?: any}> {
  try {
    console.log('Testing server connection using cached health check...');
    
    // Use cached health check first
    const cachedStatus = healthCheckService.getCachedStatus();
    if (cachedStatus) {
      console.log('Using cached health status:', cachedStatus);
      return {
        success: cachedStatus.isHealthy,
        error: cachedStatus.error,
        details: cachedStatus
      };
    }

    // If no cache, perform new check
    const healthResult = await healthCheckService.getHealthStatus();
    console.log('Fresh health check result:', healthResult);
    
    return {
      success: healthResult.isHealthy,
      error: healthResult.error,
      details: healthResult
    };
  } catch (error) {
    console.error('Server connection test failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown connection error',
      details: error
    };
  }
}

// Save quote data to server
export async function saveQuoteDataToServer(
  header: QuoteHeader,
  items: QuoteItem[],
  positions: Position[] = [],
  gewerkOrder: string[] = [],
  customFilename?: string,
  editorState?: PersistedQuoteEditorState
): Promise<ApiResponse> {
  try {
    console.log('Saving quote data to server...');
    
    const quoteDataPackage: QuoteDataPackage = {
      header,
      items,
      positions,
      gewerkOrder,
      editorState: editorState ? normalizePersistedQuoteEditorState(editorState) : undefined,
      metadata: {
        filename: customFilename,
        customerName: getQuoteCustomerName({ header }),
        projectName: getQuoteProjectName({ header }),
        offerNumber: getQuoteOfferNumber({ header }),
        timestamp: new Date().toISOString(),
        version: 1,
        itemCount: items.length,
        // Auftragssumme netto = nur Hauptpositionen (Bedarf/Alternativ ausgeschlossen),
        // identisch zur Server-Persistenz (json-storage computeQuoteTotals().netMain).
        totalValue: computeQuoteTotals(items).netMain,
        totalPositions: positions.length
      }
    };

    const response = await fetch(`${getServerUrl()}/api/quote-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(quoteDataPackage),
    });

    const result = await response.json();
    console.log('Quote data save response:', result);

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return result;
  } catch (error) {
    console.error('Error saving quote data:', error);
    throw error;
  }
}

// Object-based revision-aware save contract for all new callers.
export async function saveQuoteData(
  request: SaveQuoteRequest,
): Promise<QuoteDataPackage> {
  try {
    const response = await fetch(`${getServerUrl()}/api/quote-data/save-and-get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...request,
        editorState: normalizePersistedQuoteEditorState(request.editorState),
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to save and get quote data');
    }

    console.log('Quote data saved and retrieved atomically');
    return result.data;
  } catch (error) {
    console.error('Error in atomic save and get:', error);
    throw error;
  }
}

// Der positionale Kompatibilitäts-Wrapper `saveAndGetQuoteData` ist entfernt:
// repo-weit ohne Aufrufer, und er reichte kein `expectedStorageRevision` durch,
// war also ein CAS-freier Nebeneingang neben dem Save-Coordinator.
// Alle Aufrufer nutzen `saveQuoteData(request)` mit `buildQuoteSaveRequest(...)`.

// Get quote data for DOCX generation (validated and ready)
export async function getQuoteDataForDocx(): Promise<QuoteDataPackage> {
  try {
    const response = await fetch(`${getServerUrl()}/api/docx/quote-data`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to get quote data for DOCX');
    }

    console.log('Retrieved validated quote data for DOCX generation');
    return result.data;
  } catch (error) {
    console.error('Error getting quote data for DOCX:', error);
    throw error;
  }
}

// Get latest quote data from server
export async function getLatestQuoteData(): Promise<QuoteDataPackage> {
  try {
    const response = await fetch(`${getServerUrl()}/api/quote-data/latest`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'No quote data available');
    }

    return result.data;
  } catch (error) {
    console.error('Error getting latest quote data:', error);
    throw error;
  }
}

// List all stored quote data files - optimized without redundant health checks
export async function getStoredQuoteDataList(): Promise<ApiResponse> {
  try {
    console.log('Fetching quote data list from:', `${getServerUrl()}/api/quote-data`);
    
    const response = await fetch(`${getServerUrl()}/api/quote-data`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    console.log('Quote data response status:', response.status, response.statusText);
    
    const result = await response.json();
    console.log('Quote data response:', result);
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return result;
  } catch (error) {
    console.error('Error getting quote data list:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      serverUrl: getServerUrl()
    });
    throw error;
  }
}

// Get specific quote data by slot
export async function getQuoteDataBySlot(slot: number): Promise<QuoteDataPackage> {
  try {
    console.log(`Fetching quote data from slot ${slot}...`);
    
    const response = await fetch(`${getServerUrl()}/api/quote-data/${slot}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    console.log(`Quote data slot ${slot} response:`, result);

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return result.data;
  } catch (error) {
    console.error(`Error getting quote data from slot ${slot}:`, error);
    throw error;
  }
}

// Delete quote data by slot
export async function deleteQuoteDataBySlot(slot: number): Promise<ApiResponse> {
  try {
    console.log(`Deleting quote data from slot ${slot}...`);
    
    const response = await fetch(`${getServerUrl()}/api/quote-data/${slot}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    console.log(`Delete slot ${slot} response:`, result);

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return result;
  } catch (error) {
    console.error(`Error deleting quote data from slot ${slot}:`, error);
    throw error;
  }
}

export async function duplicateQuoteDataBySlot(
  slot: number,
): Promise<DuplicateQuoteResult> {
  const response = await fetch(`${getServerUrl()}/api/quote-data/${slot}/duplicate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return result.data;
}

// Helper function to validate quote data before sending
export function validateQuoteData(header: QuoteHeader, items: QuoteItem[], positions?: Position[]): boolean {
  if (!header || !items || !Array.isArray(items)) {
    return false;
  }
  
  if (items.length === 0) {
    return false;
  }
  
  // For import functionality, we also need positions
  if (positions && !Array.isArray(positions)) {
    return false;
  }
  
  // Check that all items have required fields
  for (const item of items) {
    if (!item.beschreibung || typeof item.preis !== 'number' || typeof item.menge !== 'number') {
      return false;
    }
  }
  
  return true;
} 

export async function updateQuoteFolders(updates: Array<{ slot: number; folderId?: string | null; order?: number }>): Promise<void> {
  const response = await fetch(`${getServerUrl()}/api/quote-data/update-folders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ updates }),
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
  }
}
